import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/UI';
import { trpc } from '@/api/trpc';
import type { EntryScorecardsRow } from '@/types/appRouter';
import { formatTimeHorizon, getEventHorizonTooltip, isEventHorizon } from '@/utils/timeHorizonGenerator';
import { getScorecardInitializationClassName, getScorecardInitializationLabel } from './helpers';

interface LinkedExperimentCardProps {
    scorecard: EntryScorecardsRow;
    isOwnedScorecard: boolean;
}

const LinkedExperimentCard = ({ scorecard, isOwnedScorecard }: LinkedExperimentCardProps) => {
    // Fetch experiment info for display
    const {
        data: experimentInfo,
        isLoading: experimentLoading,
        error: experimentError
    } = useQuery({
        ...trpc.experiment.getById.queryOptions({
            experimentId: scorecard.experimentId ?? 0
        }),
        enabled: isOwnedScorecard && !!scorecard.experimentId
    });

    return (
        <Card className="p-4">
            <h3 className="text-white text-lg font-semibold mb-4">Linked Experiment</h3>
            {experimentLoading ? (
                <div className="text-zinc-400 text-sm">Loading experiment info...</div>
            ) : experimentError ? (
                <div className="text-red-400 text-sm">Failed to load experiment: {experimentError.message}</div>
            ) : experimentInfo ? (
                <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Experiment</div>
                            <div className="text-white font-medium">
                                #{experimentInfo.id} - {experimentInfo.name}
                            </div>
                            {experimentInfo.description && <div className="text-sm text-zinc-400 mt-1">{experimentInfo.description}</div>}
                        </div>
                        <div>
                            <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Configuration</div>
                            <div className="text-sm text-zinc-300 space-y-1">
                                <div>
                                    Duration: <span className="text-white font-medium">{scorecard.duration}</span>
                                </div>
                                <div>
                                    Exit Horizon:{' '}
                                    {(() => {
                                        const seconds = scorecard.horizonSeconds;
                                        const label = formatTimeHorizon(seconds);

                                        const tooltip = isEventHorizon(seconds) ? getEventHorizonTooltip(seconds) : null;

                                        const isOffset = seconds > 0;
                                        const days = isOffset ? seconds / 86400 : 0;

                                        return (
                                            <span className="text-white font-medium" title={tooltip ?? undefined}>
                                                {label}
                                                {isOffset && days >= 1 ? ` (${days.toFixed(1)} days)` : ''}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="pt-2 border-t border-zinc-800">
                        <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Initialization Status</div>
                        <div className="flex items-center gap-3 text-sm">
                            <span className={getScorecardInitializationClassName(scorecard.initializationStatus)}>
                                {getScorecardInitializationLabel(scorecard.initializationStatus)}
                            </span>
                            {scorecard.entitiesProcessed != null && scorecard.totalEntities != null && (
                                <span className="text-zinc-400">
                                    ({scorecard.entitiesProcessed}/{scorecard.totalEntities} entities)
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="pt-2 border-t border-zinc-800">
                        <div className="text-xs text-zinc-400 italic">
                            Note: Experiment, duration, and horizon cannot be changed after scorecard creation as they determine the
                            baseline data used for scoring.
                        </div>
                    </div>
                </div>
            ) : null}
        </Card>
    );
};

export default LinkedExperimentCard;
